import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import {
    Download, ExternalLink, File as FileIcon, FileArchive, FileAudio, FileImage, FileSpreadsheet, FileText, FileVideo,
    FileX, FolderOpen, Link2, Loader2, MoreHorizontal, Presentation
} from "@fg/icons";
import { useAccessToken } from "@/auth/useAccessToken";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import {
    DriveNotFoundError, createDownloadLink, downloadDriveFile, driveOpenUrl, editorUrl, fetchThumbnail, fileKind,
    formatBytes, getDriveItem, type FileKind
} from "@/lib/drive";
import { classifyLegacyHref, type FileRef } from "@/lib/fileMarkers";
import { cn } from "@/lib/utils";

const PREVIEWABLE: FileKind[] = ["image", "video", "audio", "pdf"];

/** Solid app-tile colours for files that open in a suite editor (Docs / Sheets / Slides / PDF). */
const APP_TILE: Partial<Record<FileKind, string>> = {
    word: "bg-[#185abd]",
    excel: "bg-[#107c41]",
    powerpoint: "bg-[#c43e1c]",
    pdf: "bg-[#c42b1c]"
};

export function FileTypeIcon({ kind, className }: { kind: FileKind; className?: string }) {
    const cls = cn("shrink-0", className);
    switch (kind) {
        case "word": return <FileText className={cn(cls, "text-blue-600 dark:text-blue-400")} aria-hidden />;
        case "excel": return <FileSpreadsheet className={cn(cls, "text-emerald-600 dark:text-emerald-400")} aria-hidden />;
        case "powerpoint": return <Presentation className={cn(cls, "text-orange-600 dark:text-orange-400")} aria-hidden />;
        case "pdf": return <FileText className={cn(cls, "text-red-600 dark:text-red-400")} aria-hidden />;
        case "image": return <FileImage className={cn(cls, "text-violet-600 dark:text-violet-400")} aria-hidden />;
        case "video": return <FileVideo className={cn(cls, "text-pink-600 dark:text-pink-400")} aria-hidden />;
        case "audio": return <FileAudio className={cn(cls, "text-amber-600 dark:text-amber-400")} aria-hidden />;
        case "archive": return <FileArchive className={cn(cls, "text-yellow-700 dark:text-yellow-400")} aria-hidden />;
        case "text": return <FileText className={cn(cls, "text-muted-foreground")} aria-hidden />;
        default: return <FileIcon className={cn(cls, "text-muted-foreground")} aria-hidden />;
    }
}

/** The Drive item behind a file card — resolved lazily; 404 means deleted or access removed. */
export function useDriveItem(id: string) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["drive-item", id, token],
        queryFn: () => getDriveItem(id, token),
        enabled: Boolean(token),
        staleTime: 60_000,
        retry: (count, err) => !(err instanceof DriveNotFoundError) && count < 2
    });
}

/**
 * The files in one message: pictures as preview tiles, everything else as
 * Teams-style cards — a compact grid when there are several.
 */
export function FileAttachmentList({ files }: { files: FileRef[] }) {
    if (files.length === 0) return null;
    // A file from Connect's own upload store (older messages) has no Drive item to thumbnail: it is always a card.
    const images = files.filter((f) => !f.url && fileKind(f.name, f.type) === "image");
    const others = files.filter((f) => f.url || fileKind(f.name, f.type) !== "image");
    return (
        <div className="mt-1 flex flex-col gap-1.5">
            {images.length > 0 ? (
                <div className={cn("grid gap-1.5", images.length === 1 ? "grid-cols-1" : "grid-cols-2")}>
                    {images.map((f) => <ImageTile key={f.id} file={f} />)}
                </div>
            ) : null}
            {others.length > 0 ? (
                <div className={cn("grid gap-1.5", others.length > 1 && "sm:grid-cols-2")}>
                    {others.map((f) => (f.url ? <UploadFileCard key={f.id} file={f} /> : <DriveFileCard key={f.id} file={f} />))}
                </div>
            ) : null}
        </div>
    );
}

function useFileActions(file: FileRef) {
    const token = useAccessToken();
    const auth = useAuth();
    const myEmail = (auth.user?.profile.email ?? "").toLowerCase();
    const item = useDriveItem(file.id);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    const name = item.data?.name ?? file.name;
    const size = item.data?.sizeBytes ?? file.size;
    const contentType = item.data?.contentType ?? file.type;
    const kind = fileKind(name, contentType);
    // Absent = you own it; false = view-only share with download blocked.
    const canDownload = item.data?.canDownload !== false;
    const owner = item.data
        ? (item.data.ownerEmail ?? "you")
        : (file.owner && file.owner.toLowerCase() !== myEmail ? file.owner : "you");
    const editUrl = editorUrl(file.id, name, kind);
    const unavailable = item.error instanceof DriveNotFoundError;

    const download = async () => {
        setBusy(true);
        setError(null);
        try {
            await downloadDriveFile(file.id, token);
        } catch (err) {
            setError(err instanceof DriveNotFoundError
                ? "File unavailable (deleted or access removed)"
                : err instanceof Error ? err.message : "Download failed");
        } finally {
            setBusy(false);
        }
    };

    const open = async () => {
        setError(null);
        if (editUrl) {
            window.open(editUrl, "_blank", "noopener,noreferrer");
        } else if (PREVIEWABLE.includes(kind)) {
            setPreviewOpen(true);
        } else if (canDownload) {
            await download();
        } else {
            openInDrive();
        }
    };

    // Drive's universal link opens this exact file for anyone it's shared with.
    const driveUrl = driveOpenUrl(file.id);
    const openInDrive = () => window.open(driveUrl, "_blank", "noopener,noreferrer");

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(editUrl ?? driveUrl);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            setError("Couldn't copy the link");
        }
    };

    return {
        item, name, size, contentType, kind, canDownload, owner, editUrl, unavailable,
        previewOpen, setPreviewOpen, busy, error, copied, open, download, copyLink, openInDrive
    };
}

type FileActions = ReturnType<typeof useFileActions>;

function ActionButtons({ a, className }: { a: FileActions; className?: string }) {
    return (
        <div className={cn("flex shrink-0 items-center gap-0.5", className)}>
            {a.canDownload ? (
                <button
                    type="button"
                    onClick={() => void a.download()}
                    disabled={a.busy}
                    aria-label={`Download ${a.name}`}
                    title="Download"
                    className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
                >
                    {a.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                </button>
            ) : null}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        aria-label={`More options for ${a.name}`}
                        title="More options"
                        className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                    >
                        <MoreHorizontal className="h-3.5 w-3.5" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem onSelect={() => void a.open()} className="text-xs">
                        <ExternalLink className="mr-2 h-3.5 w-3.5" />
                        {a.editUrl ? "Open in editor" : "Open"}
                    </DropdownMenuItem>
                    {a.canDownload ? (
                        <DropdownMenuItem onSelect={() => void a.download()} className="text-xs">
                            <Download className="mr-2 h-3.5 w-3.5" /> Download
                        </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={a.openInDrive} className="text-xs">
                        <FolderOpen className="mr-2 h-3.5 w-3.5" /> Open in Drive
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => void a.copyLink()} className="text-xs">
                        <Link2 className="mr-2 h-3.5 w-3.5" /> {a.copied ? "Link copied" : "Copy link"}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}

function UnavailableCard({ name }: { name: string }) {
    return (
        <div className="flex w-full max-w-sm items-center gap-3 rounded-lg border border-dashed bg-muted/30 p-2.5 text-muted-foreground">
            <FileX className="h-8 w-8 shrink-0" aria-hidden />
            <div className="min-w-0">
                <div className="truncate text-sm line-through" title={name}>{name}</div>
                <div className="text-[11px]">File unavailable (deleted or access removed)</div>
            </div>
        </div>
    );
}

/**
 * One file in a message, Teams-style: type icon, name, size, who shared it,
 * and Open / Download / Open in Drive / Copy link. `detail` adds a line (a
 * recording's duration, say).
 */
export function DriveFileCard({ file, detail, className }: { file: FileRef; detail?: React.ReactNode; className?: string }) {
    const a = useFileActions(file);
    if (a.unavailable) return <UnavailableCard name={file.name} />;
    return (
        <div className={cn("w-full max-w-sm", className)}>
            <div className="flex items-center gap-2.5 rounded-lg border border-[var(--connect-line)] bg-card px-2.5 py-2 text-card-foreground">
                <button
                    type="button"
                    onClick={() => void a.open()}
                    className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    aria-label={`Open ${a.name}`}
                    title={a.editUrl ? "Open in a new tab" : "Open"}
                >
                    {/* The suite's app tile (Docs / Sheets / Slides / PDF colours) as in the mock-up. */}
                    <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-md", APP_TILE[a.kind] ?? "bg-[var(--connect-subtle)]")}>
                        <FileTypeIcon kind={a.kind} className={cn("h-[18px] w-[18px]", APP_TILE[a.kind] && "!text-white")} />
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium hover:underline">{a.name}</span>
                        <span className="block truncate text-[11px] text-muted-foreground">
                            {[formatBytes(a.size), `Shared by ${a.owner}`, a.canDownload ? null : "View only"].filter(Boolean).join(" · ")}
                        </span>
                        {detail ? <span className="block truncate text-[11px] text-muted-foreground">{detail}</span> : null}
                    </span>
                    {a.item.isPending ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" aria-label="Loading" /> : null}
                </button>
                <ActionButtons a={a} />
            </div>
            {a.error ? <p role="alert" className="mt-0.5 px-1 text-[11px] text-destructive">{a.error}</p> : null}
            {a.previewOpen ? (
                <FilePreviewDialog
                    id={file.id}
                    name={a.name}
                    kind={a.kind}
                    canDownload={a.canDownload}
                    onDownload={() => void a.download()}
                    onClose={() => a.setPreviewOpen(false)}
                />
            ) : null}
        </div>
    );
}

/**
 * A file that older clients (Mail's chat dock) posted as a link into Connect's own upload store, not
 * into Drive. Same card as a Drive file: type tile, name, size, "Shared by", Download and a "..." menu.
 * The link was checked when the message was read (only http(s) on the Connect / Drive API host or this
 * page's origin), and is checked again here before the token is sent to it.
 */
export function UploadFileCard({ file, className }: { file: FileRef; className?: string }) {
    const token = useAccessToken();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const target = classifyLegacyHref(file.url);
    const url = target?.kind === "upload" ? target.url : null;
    const kind = fileKind(file.name, file.type);

    const download = async () => {
        if (!url) return;
        setBusy(true);
        setError(null);
        try {
            const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (res.status === 404) throw new Error("File unavailable (deleted or access removed)");
            if (!res.ok) throw new Error(`Download failed (${res.status})`);
            const blob = await res.blob();
            const href = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = href;
            a.download = file.name;
            a.rel = "noopener";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.setTimeout(() => URL.revokeObjectURL(href), 10_000);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Download failed");
        } finally {
            setBusy(false);
        }
    };

    const copyLink = async () => {
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            setError("Couldn't copy the link");
        }
    };

    if (!url) return <UnavailableCard name={file.name} />;
    return (
        <div className={cn("w-full max-w-sm", className)}>
            <div className="flex items-center gap-2.5 rounded-lg border border-[var(--connect-line)] bg-card px-2.5 py-2 text-card-foreground">
                <button
                    type="button"
                    onClick={() => void download()}
                    className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    aria-label={`Download ${file.name}`}
                    title="Download"
                >
                    <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-md", APP_TILE[kind] ?? "bg-[var(--connect-subtle)]")}>
                        <FileTypeIcon kind={kind} className={cn("h-[18px] w-[18px]", APP_TILE[kind] && "!text-white")} />
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium hover:underline">{file.name}</span>
                        <span className="block truncate text-[11px] text-muted-foreground">
                            {[file.size > 0 ? formatBytes(file.size) : null, file.owner ? `Shared by ${file.owner}` : null].filter(Boolean).join(" \u00b7 ")}
                        </span>
                    </span>
                </button>
                <div className="flex shrink-0 items-center gap-0.5">
                    <button
                        type="button"
                        onClick={() => void download()}
                        disabled={busy}
                        aria-label={`Download ${file.name}`}
                        title="Download"
                        className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
                    >
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    </button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                aria-label={`More options for ${file.name}`}
                                title="More options"
                                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                            >
                                <MoreHorizontal className="h-3.5 w-3.5" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem onSelect={() => void download()} className="text-xs">
                                <Download className="mr-2 h-3.5 w-3.5" /> Download
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => void copyLink()} className="text-xs">
                                <Link2 className="mr-2 h-3.5 w-3.5" /> {copied ? "Link copied" : "Copy link"}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
            {error ? <p role="alert" className="mt-0.5 px-1 text-[11px] text-destructive">{error}</p> : null}
        </div>
    );
}

const ROW_ACTION =
    "flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50";

/**
 * One file in a conversation's Files tab: type icon, name, who shared it and
 * when, size, and Open / Download (when allowed) / Open in Drive. The same
 * actions as the message card, laid out as a list row.
 */
export function DriveFileRow({ file, sharedBy, sharedAt }: { file: FileRef; sharedBy: string; sharedAt: string }) {
    const a = useFileActions(file);
    const when = new Date(sharedAt);
    const whenLabel = Number.isNaN(when.getTime())
        ? ""
        : when.toLocaleDateString(undefined, { day: "numeric", month: "short", year: when.getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
    return (
        <li className="border-b last:border-b-0">
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 px-3 py-2 hover:bg-accent/40 md:grid-cols-[minmax(0,1fr)_minmax(0,10rem)_6rem_5rem_auto]">
                <button
                    type="button"
                    onClick={() => void a.open()}
                    disabled={a.unavailable}
                    aria-label={`Open ${a.name}`}
                    className="flex min-w-0 items-center gap-2.5 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-default"
                >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted/60">
                        {a.unavailable ? <FileX className="h-4 w-4 text-muted-foreground" aria-hidden /> : <FileTypeIcon kind={a.kind} className="h-4 w-4" />}
                    </span>
                    <span className="min-w-0">
                        <span className={cn("block truncate text-sm font-medium", a.unavailable ? "text-muted-foreground line-through" : "hover:underline")} title={a.name}>
                            {a.name}
                        </span>
                        {/* Phones: the other columns fold into this line. */}
                        <span className="block truncate text-[11px] text-muted-foreground md:hidden">
                            {a.unavailable
                                ? "Unavailable (deleted or access removed)"
                                : [sharedBy, whenLabel, formatBytes(a.size), a.canDownload ? null : "View only"].filter(Boolean).join(" · ")}
                        </span>
                        {a.unavailable ? (
                            <span className="hidden text-[11px] text-muted-foreground md:block">Unavailable (deleted or access removed)</span>
                        ) : !a.canDownload ? (
                            <span className="hidden text-[11px] text-muted-foreground md:block">View only</span>
                        ) : null}
                    </span>
                </button>
                <span className="hidden truncate text-xs text-muted-foreground md:block" title={sharedBy}>{sharedBy}</span>
                <time className="hidden text-xs text-muted-foreground md:block" dateTime={sharedAt} title={when.toLocaleString()}>{whenLabel}</time>
                <span className="hidden text-xs text-muted-foreground md:block">{formatBytes(a.size)}</span>
                <div className="flex shrink-0 items-center gap-0.5">
                    {a.item.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin text-muted-foreground" aria-label="Loading" /> : null}
                    <button type="button" onClick={() => void a.open()} disabled={a.unavailable} aria-label={`Open ${a.name}`} title={a.editUrl ? "Open in editor" : "Open"} className={ROW_ACTION}>
                        <ExternalLink className="h-3.5 w-3.5" />
                    </button>
                    {a.canDownload && !a.unavailable ? (
                        <button type="button" onClick={() => void a.download()} disabled={a.busy} aria-label={`Download ${a.name}`} title="Download" className={ROW_ACTION}>
                            {a.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                        </button>
                    ) : null}
                    <button type="button" onClick={a.openInDrive} disabled={a.unavailable} aria-label={`Open ${a.name} in Drive`} title="Open in Drive" className={ROW_ACTION}>
                        <FolderOpen className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
            {a.error ? <p role="alert" className="px-3 pb-1.5 text-[11px] text-destructive">{a.error}</p> : null}
            {a.previewOpen ? (
                <FilePreviewDialog
                    id={file.id}
                    name={a.name}
                    kind={a.kind}
                    canDownload={a.canDownload}
                    onDownload={() => void a.download()}
                    onClose={() => a.setPreviewOpen(false)}
                />
            ) : null}
        </li>
    );
}

/** A picture in a message: the Drive thumbnail as a tile; click for the full image. */
function ImageTile({ file }: { file: FileRef }) {
    const token = useAccessToken();
    const a = useFileActions(file);
    const [thumb, setThumb] = useState<string | null>(null);
    const [thumbState, setThumbState] = useState<"loading" | "ready" | "none">("loading");

    useEffect(() => {
        if (!token) return;
        const ctrl = new AbortController();
        let objectUrl: string | null = null;
        setThumbState("loading");
        fetchThumbnail(file.id, token, ctrl.signal)
            .then((blob) => {
                if (ctrl.signal.aborted) return;
                if (!blob) { setThumbState("none"); return; }
                objectUrl = URL.createObjectURL(blob);
                setThumb(objectUrl);
                setThumbState("ready");
            })
            .catch(() => { if (!ctrl.signal.aborted) setThumbState("none"); });
        return () => {
            ctrl.abort();
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [file.id, token]);

    if (a.unavailable) return <UnavailableCard name={file.name} />;
    if (thumbState === "none") return <DriveFileCard file={file} />;
    return (
        <figure className="group/tile relative w-full max-w-xs overflow-hidden rounded-lg border bg-muted/30">
            <button
                type="button"
                onClick={() => a.setPreviewOpen(true)}
                aria-label={`Preview ${a.name}`}
                className="block w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
                {thumbState === "ready" && thumb ? (
                    <img src={thumb} alt={a.name} className="max-h-64 w-full object-cover" />
                ) : (
                    <span className="flex h-32 items-center justify-center">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Loading preview" />
                    </span>
                )}
            </button>
            <figcaption className="flex items-center gap-1 border-t bg-background/90 py-0.5 pl-2 pr-0.5 text-[11px] text-muted-foreground">
                <span className="min-w-0 flex-1 truncate" title={a.name}>{a.name} · {formatBytes(a.size)}</span>
                <ActionButtons a={a} />
            </figcaption>
            {a.error ? <p role="alert" className="px-2 pb-1 text-[11px] text-destructive">{a.error}</p> : null}
            {a.previewOpen ? (
                <FilePreviewDialog
                    id={file.id}
                    name={a.name}
                    kind={a.kind}
                    canDownload={a.canDownload}
                    onDownload={() => void a.download()}
                    onClose={() => a.setPreviewOpen(false)}
                />
            ) : null}
        </figure>
    );
}

/**
 * In-app preview: images, video and audio stream from a short-lived Drive
 * "preview" link (allowed for view-only people too); a PDF is fetched and
 * shown from a blob typed application/pdf, so whatever the server says the
 * type is, the browser only ever treats it as a PDF.
 */
export function FilePreviewDialog({ id, name, kind, canDownload, onDownload, onClose }: {
    id: string;
    name: string;
    kind: FileKind;
    canDownload: boolean;
    onDownload: () => void;
    onClose: () => void;
}) {
    const token = useAccessToken();
    const [src, setSrc] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        let objectUrl: string | null = null;
        setSrc(null);
        setError(null);
        (async () => {
            try {
                const link = await createDownloadLink(id, "preview", token);
                if (kind !== "pdf") {
                    if (!cancelled) setSrc(link);
                    return;
                }
                const res = await fetch(link, { credentials: "omit" });
                if (!res.ok) throw new Error(`Drive returned ${res.status}`);
                const bytes = await res.arrayBuffer();
                if (cancelled) return;
                objectUrl = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
                setSrc(objectUrl);
            } catch (err) {
                if (!cancelled) {
                    setError(err instanceof DriveNotFoundError
                        ? "File unavailable (deleted or access removed)"
                        : err instanceof Error ? err.message : "Couldn't load the preview");
                }
            }
        })();
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [id, kind, token]);

    return (
        <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent className="flex max-h-[92vh] w-[96vw] max-w-5xl flex-col gap-3 p-4 sm:p-5">
                <div className="flex items-center gap-2 pr-8">
                    <FileTypeIcon kind={kind} className="h-5 w-5" />
                    <DialogTitle className="min-w-0 flex-1 truncate text-base">{name}</DialogTitle>
                    {canDownload ? (
                        <button
                            type="button"
                            onClick={onDownload}
                            className="flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs hover:bg-accent"
                        >
                            <Download className="h-3.5 w-3.5" /> Download
                        </button>
                    ) : null}
                    <button
                        type="button"
                        onClick={() => window.open(driveOpenUrl(id), "_blank", "noopener,noreferrer")}
                        className="flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs hover:bg-accent"
                    >
                        <FolderOpen className="h-3.5 w-3.5" /> Open in Drive
                    </button>
                </div>
                <DialogDescription className="sr-only">Preview of {name}</DialogDescription>
                <div className="flex min-h-[40vh] flex-1 items-center justify-center overflow-auto rounded-md bg-muted/40">
                    {error ? (
                        <p role="alert" className="p-6 text-sm text-destructive">{error}</p>
                    ) : !src ? (
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading preview" />
                    ) : kind === "image" ? (
                        <img src={src} alt={name} className="max-h-[78vh] max-w-full object-contain" />
                    ) : kind === "video" ? (
                        <video src={src} controls autoPlay playsInline className="max-h-[78vh] w-full bg-black" />
                    ) : kind === "audio" ? (
                        <audio src={src} controls autoPlay className="w-full max-w-lg p-6" />
                    ) : (
                        <iframe src={src} title={name} className="h-[78vh] w-full rounded-md border-0 bg-white" />
                    )}
                </div>
                {!canDownload ? (
                    <p className="text-[11px] text-muted-foreground">The owner shared this as view only — downloading is turned off.</p>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}
